import React, { useState, useEffect } from "react";
import { Leaf, CheckCircle, ArrowRight, Sparkles, Zap, Shield } from "lucide-react";
import PublicHeader from ".././public/PublicHeader";
import AuthModal from "../../components/AuthModal";
import './LandingPage.css';

const LandingPage = () => {
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("mousemove", handleMouseMove);
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const features = [
    {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
          <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="currentColor"/>
          <path d="M19 15L20.09 18.26L24 19L20.09 19.74L19 23L17.91 19.74L14 19L17.91 18.26L19 15Z" fill="currentColor"/>
        </svg>
      ),
      title: "AI Crop Advisory",
      desc: "Smart crop recommendations based on soil, weather & regional insights with machine learning algorithms",
      color: "#10b981",
      gradient: "linear-gradient(135deg, #10b981, #059669)"
    },
    {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
          <path d="M12 2l3.09 6.26L22 9l-6.91 1.01L12 16l-3.09-5.99L2 9l6.91-1.74L12 2z" fill="currentColor"/>
          <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" fill="none"/>
          <path d="M8 12l2 2 4-4" stroke="white" strokeWidth="2" fill="none"/>
        </svg>
      ),
      title: "Satellite Monitoring",
      desc: "Real-time satellite detection of harvested & cultivated farmland with precision mapping",
      color: "#3b82f6",
      gradient: "linear-gradient(135deg, #3b82f6, #1d4ed8)"
    },
    {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
          <path d="M12 2c-5.33 4.55-8 8.48-8 11.8 0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8z" fill="currentColor"/>
          <path d="M12 18c-2.57 0-4.33-1.43-4.33-3.2 0-1.32 1.67-3.25 4.33-5.8 2.67 2.55 4.33 4.48 4.33 5.8 0 1.77-1.76 3.2-4.33 3.2z" fill="white" opacity="0.3"/>
        </svg>
      ),
      title: "Groundwater Intelligence",
      desc: "State, district & village-level groundwater analytics with predictive forecasting",
      color: "#0ea5e9",
      gradient: "linear-gradient(135deg, #0ea5e9, #0284c7)"
    },
    {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
          <path d="M2 12h20M12 2v20" stroke="currentColor" strokeWidth="2"/>
          <path d="M7 7h10v10H7z" fill="none" stroke="currentColor" strokeWidth="2"/>
          <circle cx="9" cy="9" r="1" fill="currentColor"/>
          <circle cx="15" cy="9" r="1" fill="currentColor"/>
          <circle cx="9" cy="15" r="1" fill="currentColor"/>
          <circle cx="15" cy="15" r="1" fill="currentColor"/>
          <path d="M6 18l12-12" stroke="currentColor" strokeWidth="1" opacity="0.5"/>
        </svg>
      ),
      title: "Real-Time Mandi Prices",
      desc: "Live market prices from 1000+ mandis across India with price trends and forecasting",
      color: "#8b5cf6",
      gradient: "linear-gradient(135deg, #8b5cf6, #7c3aed)"
    },
    {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
          <path d="M3 3h18v18H3z" fill="none" stroke="currentColor" strokeWidth="2"/>
          <path d="M9 9h6v6H9z" fill="currentColor" opacity="0.3"/>
          <path d="M7 7l10 10M17 7l-10 10" stroke="currentColor" strokeWidth="1.5"/>
          <circle cx="12" cy="12" r="2" fill="currentColor"/>
          <path d="M12 8v8M8 12h8" stroke="white" strokeWidth="1"/>
        </svg>
      ),
      title: "Crop Disease Scanner",
      desc: "Upload crop images for instant disease identification with treatment recommendations",
      color: "#ef4444",
      gradient: "linear-gradient(135deg, #ef4444, #dc2626)"
    },
    {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
          <path d="M12 2L2 7l10 5 10-5-10-5z" fill="currentColor" opacity="0.3"/>
          <path d="M2 17l10 5 10-5" fill="none" stroke="currentColor" strokeWidth="2"/>
          <path d="M2 12l10 5 10-5" fill="none" stroke="currentColor" strokeWidth="2"/>
          <circle cx="12" cy="12" r="2" fill="currentColor"/>
          <path d="M8 8l8 8M16 8l-8 8" stroke="currentColor" strokeWidth="1" opacity="0.5"/>
        </svg>
      ),
      title: "Weather Intelligence",
      desc: "15-day weather forecasts, rainfall predictions, and climate risk assessments for farming",
      color: "#84cc16",
      gradient: "linear-gradient(135deg, #84cc16, #65a30d)"
    },
  ];

  const benefits = [
    { 
      text: "Real-time satellite monitoring", 
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="currentColor"/>
          <path d="M19 15L20.09 18.26L24 19L20.09 19.74L19 23L17.91 19.74L14 19L17.91 18.26L19 15Z" fill="currentColor"/>
          <path d="M5 15L6.09 18.26L10 19L6.09 19.74L5 23L3.91 19.74L0 19L3.91 18.26L5 15Z" fill="currentColor"/>
        </svg>
      ),
      desc: "Monitor crop health and field conditions with precision satellite imagery updated daily"
    },
    { 
      text: "AI-powered crop predictions", 
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M9 11H7l3-7 3 7h-2v8h-2v-8z" fill="currentColor"/>
          <path d="M13 2L15.5 6.5L20 9L15.5 11.5L13 16L10.5 11.5L6 9L10.5 6.5L13 2Z" fill="currentColor" opacity="0.7"/>
          <circle cx="17" cy="17" r="3" fill="currentColor" opacity="0.5"/>
        </svg>
      ),
      desc: "Get accurate yield predictions and optimal planting recommendations using machine learning"
    },
    { 
      text: "Live mandi prices from 1000+ markets", 
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M2 12h20M12 2v20" stroke="currentColor" strokeWidth="2"/>
          <path d="M7 7h10v10H7z" fill="none" stroke="currentColor" strokeWidth="2"/>
          <circle cx="9" cy="9" r="1" fill="currentColor"/>
          <circle cx="15" cy="9" r="1" fill="currentColor"/>
          <circle cx="9" cy="15" r="1" fill="currentColor"/>
          <circle cx="15" cy="15" r="1" fill="currentColor"/>
        </svg>
      ),
      desc: "Access real-time market prices and trends to make informed selling decisions"
    },
    { 
      text: "Instant crop disease identification", 
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M3 3h18v18H3z" fill="none" stroke="currentColor" strokeWidth="2"/>
          <path d="M9 9h6v6H9z" fill="currentColor" opacity="0.3"/>
          <path d="M7 7l10 10M17 7l-10 10" stroke="currentColor" strokeWidth="1.5"/>
          <circle cx="12" cy="12" r="2" fill="currentColor"/>
        </svg>
      ),
      desc: "Upload crop photos for instant disease diagnosis with treatment recommendations"
    },
    { 
      text: "Increase productivity by 35%", 
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6h-6z" fill="currentColor"/>
          <circle cx="18" cy="8" r="2" fill="currentColor" opacity="0.7"/>
        </svg>
      ),
      desc: "Proven results from farmers using our platform show significant yield improvements"
    },
    { 
      text: "Reduce water usage by 40%", 
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 2c-5.33 4.55-8 8.48-8 11.8 0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8z" fill="currentColor"/>
          <path d="M12 18c-2.57 0-4.33-1.43-4.33-3.2 0-1.32 1.67-3.25 4.33-5.8 2.67 2.55 4.33 4.48 4.33 5.8 0 1.77-1.76 3.2-4.33 3.2z" fill="white" opacity="0.3"/>
        </svg>
      ),
      desc: "Smart irrigation recommendations based on soil moisture and weather forecasts"
    },
    { 
      text: "15-day weather forecasting", 
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M6.5 20q-2.28 0-3.89-1.57Q1 16.85 1 14.58q0-1.95 1.17-3.48 1.18-1.52 3.08-1.95.63-2.3 2.5-3.72Q9.63 4 12 4q2.93 0 4.96 2.04Q19 8.07 19 11q1.73.2 2.86 1.5Q23 13.78 23 15.5q0 1.88-1.31 3.19Q20.37 20 18.5 20Z" fill="currentColor"/>
          <circle cx="12" cy="11" r="3" fill="white" opacity="0.4"/>
          <path d="M12 8v6M9 11h6" stroke="white" strokeWidth="1.5"/>
        </svg>
      ),
      desc: "Plan farming activities with accurate weather forecasts and climate risk assessments"
    },
    { 
      text: "Direct marketplace access", 
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M3 6h18l-2 13H5L3 6z" fill="none" stroke="currentColor" strokeWidth="2"/>
          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" fill="none" stroke="currentColor" strokeWidth="2"/>
          <circle cx="9" cy="12" r="1" fill="currentColor"/>
          <circle cx="15" cy="12" r="1" fill="currentColor"/>
        </svg>
      ),
      desc: "Connect directly with buyers and access agricultural equipment marketplace"
    }
  ];

  const stats = [
    { number: "50K+", label: "Active Farmers", icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
    )},
    { number: "200+", label: "Districts Covered", icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/>
        <line x1="9" y1="3" x2="9" y2="18"/>
        <line x1="15" y1="6" x2="15" y2="21"/>
      </svg>
    )},
    { number: "95%", label: "Accuracy Rate", icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
      </svg>
    )},
    { number: "24/7", label: "AI Support", icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2"/>
        <path d="M8 21h8M12 17v4"/>
        <circle cx="12" cy="10" r="2"/>
        <path d="M12 8v0M12 12v0"/>
      </svg>
    )},
  ];

  return (
    <div className="landing-page-container">
      {/* Cursor Follower */}
      <div 
        className="cursor-follower"
        style={{
          top: `${mousePosition.y - 10}px`,
          left: `${mousePosition.x - 10}px`
        }}
      />

      {/* Header */}
      <PublicHeader onLoginOpen={() => setShowAuth(true)} />

      {/* Hero Section */}
      <section className="hero-section">
        {/* Video/GIF Background */}
        <img
          src="https://media2.giphy.com/media/v1.Y2lkPTc5MGI3NjExajQ4ZmdvMXg0dnVsaXJ3MGI5ZTVoYWxxcGNuYXZkNm42MGQ5bDBkbiZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/USVgVUitCduCP9DjKo/giphy.gif"
          alt="Farming background"
          className="hero-background"
        />
        
        {/* Elegant Overlay */}
        <div className="hero-overlay" />

        {/* Subtle Animated Elements */}
        <div className="hero-animated-element-1" />
        <div className="hero-animated-element-2" />

        <div className="hero-content">
          {/* Simple Badge */}
      

          {/* Clean Main Heading */}
          <h1 className="hero-heading">
            Transform Indian Agriculture<br />
            <span>with AI Intelligence</span>
          </h1>

          {/* Clean Subtitle */}
          <p className="hero-subtitle">
            Empowering farmers, districts, and states with real-time insights,
            satellite monitoring, and smart agricultural analytics for sustainable farming.
          </p>

          {/* Simple CTA Buttons */}
          <div className="hero-cta-container"> 
            <button className="hero-cta-primary" onClick={() => setShowAuth(true)}>
              Explore Platform
              <ArrowRight size={18} />
            </button>
            
            {/* <button className="hero-cta-secondary">
              Watch Demo
            </button> */}
          </div>

          {/* Clean Stats Grid */}
          <div className="hero-stats-grid">
            {stats.map((stat, idx) => (
              <div 
                key={idx} 
                className="hero-stat-item"
              >
                <div className="hero-stat-icon">{stat.icon}</div>
                <div className="hero-stat-number">
                  {stat.number}
                </div>
                <div className="hero-stat-label">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - Simple Clean Design */}
      <section id="features" className="features-section">
        {/* Simple Background Pattern */}
        <div className="features-background-pattern" />
        
        <div className="features-content">
          {/* Section Header */}
          <div className="features-header">
            <div className="features-badge">
              <Sparkles size={16} />
              ADVANCED FEATURES
            </div>
            
            <h2 className="features-heading">
              Powerful Features for Modern Farming
            </h2>
            
            <p className="features-subtitle">
              Advanced AI-driven tools designed to maximize yield and minimize risks for sustainable agriculture
            </p>
          </div>

          {/* Simple Features Grid */}
          <div className="features-grid">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="feature-card"
                style={{
                  "--feature-color": feature.color,
                  "--feature-gradient": feature.gradient
                }}
              >
                {/* Top Accent Line */}
                <div className="feature-accent-line" />
                
                {/* Icon Container */}
                <div className="feature-icon-container">
                  {feature.icon}
                </div>
                
                {/* Content */}
                <h3 className="feature-title">
                  {feature.title}
                </h3>
                
                <p className="feature-desc">
                  {feature.desc}
                </p>
                
                {/* Learn More Link */}
                {/* <div className="feature-learn-more">
                  Learn More
                  <ArrowRight size={16} />
                </div> */}
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          
        </div>
      </section>

      {/* Auto-Moving Carousel Section */}
      <section className="carousel-section">
        <div className="carousel-container">
          <div className="carousel-header">
            
            <h2 className="carousel-heading">
              See Our Platform in Action
            </h2>
            
            <p className="carousel-subtitle">
              Real farmers, real results. Discover how our AI-powered platform is transforming agriculture across India
            </p>
          </div>

          <div className="carousel-wrapper">
            <div className="carousel-track">
              {/* Carousel Items */}
              {[
                {
                  image: "/dashboardfarmer.png",
                  title: "Farmer Dashboard",
                  desc: "Complete farming dashboard with all essential tools and insights"
                },
                {
                  image: "/cropadvisoryfarmer.png",
                  title: "Crop Advisory System",
                  desc: "AI-powered crop recommendations based on local conditions"
                },
                {
                  image: "/cropdisease.png",
                  title: "Crop Disease Detection",
                  desc: "Instant disease identification using AI-powered image analysis"
                },
                {
                  image: "/govschemes.png",
                  title: "Government Schemes",
                  desc: "Access to relevant government agricultural schemes and subsidies"
                },
                {
                  image: "/groundwaterfarmer.png",
                  title: "Groundwater Intelligence",
                  desc: "Village-level groundwater data and analytics"
                },
                {
                  image: "/mandifarmer.png",
                  title: "Mandi Price Tracker",
                  desc: "Real-time market prices from mandis across India"
                }
              ].concat([
                // Duplicate all items for seamless loop
                {
                  image: "/dashboardfarmer.png",
                  title: "Farmer Dashboard",
                  desc: "Complete farming dashboard with all essential tools and insights"
                },
                {
                  image: "/cropadvisoryfarmer.png",
                  title: "Crop Advisory System",
                  desc: "AI-powered crop recommendations based on local conditions"
                },
                {
                  image: "/cropdisease.png",
                  title: "Crop Disease Detection",
                  desc: "Instant disease identification using AI-powered image analysis"
                },
                {
                  image: "/govschemes.png",
                  title: "Government Schemes",
                  desc: "Access to relevant government agricultural schemes and subsidies"
                },
                {
                  image: "/groundwaterfarmer.png",
                  title: "Groundwater Intelligence",
                  desc: "Village-level groundwater data and analytics"
                },
                {
                  image: "/mandifarmer.png",
                  title: "Mandi Price Tracker",
                  desc: "Real-time market prices from mandis across India"
                }
              ]).map((item, index) => (
                <div key={index} className="carousel-item">
                  <div className="carousel-item-image">
                    <img src={item.image} alt={item.title} />
                    <div className="carousel-item-overlay">
                      <div className="carousel-item-play-button">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M8 5v14l11-7z" fill="white"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
      
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="benefits-section">
        {/* Background Pattern */}
        <div className="benefits-background-pattern" />
        
        <div className="benefits-content">
          {/* Section Header */}
          <div className="benefits-header">
            <div className="benefits-badge">
              <Sparkles size={16} />
              PLATFORM BENEFITS
            </div>
            
            <h2 className="benefits-heading">
              Why Farmers Choose<br />
              <span>Anuvadini AgroAI Platform</span>
            </h2>
            
            {/* <p className="benefits-subtitle">
              Join thousands of farmers who have transformed their agricultural practices with our AI-powered insights and data-driven solutions
            </p> */}
          </div>

          {/* Benefits Grid */}
          <div className="benefits-grid">
            {benefits.map((benefit, idx) => (
              <div 
                key={idx} 
                className="benefit-card"
              >
                {/* Hover Gradient */}
                <div className="benefit-hover-gradient" />
                
                <div className="benefit-content">
                  {/* Icon Container */}
                  <div className="benefit-icon-container">
                    {benefit.icon}
                  </div>
                  
                  {/* Content */}
                  <div className="benefit-text-content">
                    <h3 className="benefit-title">
                      {benefit.text}
                    </h3>
                    
                    <p className="benefit-desc">
                      {benefit.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA Section */}
         
              
              

        </div>
      </section>


      {/* Professional Footer */}
      <footer id="contact" className="pro-footer">
        <div className="pro-footer-top">
          <div className="pro-footer-brand">
            <a href="https://anuvadini.aicte.gov.in/" target="_blank" rel="noopener noreferrer" className="pro-footer-logo-link">
              <img src="/anuvadini-logo.png" alt="Anuvadini AI" className="pro-footer-logo" />
              <span className="pro-footer-brand-name">Anuvadini AgroAI</span>
            </a>
            <p className="pro-footer-tagline">
              Anuvadini AI is a human-in-the-loop Language intelligence, Artificial intelligence, Business intelligence &amp; Data intelligence company delivering secure, scalable, multilingual, multimodal platforms — supporting billions of users daily.
            </p>
            <div className="pro-footer-socials">
              <a href="https://x.com/anuvadini?s=11" target="_blank" rel="noopener noreferrer" className="pro-footer-social" aria-label="X">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622Zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
              <a href="https://www.linkedin.com/in/anuvadini-ai/" target="_blank" rel="noopener noreferrer" className="pro-footer-social" aria-label="LinkedIn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a href="https://www.youtube.com/@anuvadinineat" target="_blank" rel="noopener noreferrer" className="pro-footer-social" aria-label="YouTube">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
            </div>
          </div>

          <div className="pro-footer-links-grid">
            <div className="pro-footer-col">
              <h4 className="pro-footer-col-title">Platform Features</h4>
              <ul className="pro-footer-list">
                {["AI Crop Advisory", "Groundwater Intelligence", "Real-Time Mandi Prices", "Weather Intelligence", "Satellite Monitoring", "Farming Videos"].map((item, i) => (
                  <li key={i} className="pro-footer-list-item" onClick={() => setShowAuth(true)}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="pro-footer-col">
              <h4 className="pro-footer-col-title">Specialized Offerings</h4>
              <ul className="pro-footer-list">
                {["Crop Advisory System", "Crop Disease Detection", "Anuvadini Voice, Audio & Video", "Anuvadini GEO-AI", "Anuvadini AI Agents", "Anuvadini Drafting & OCR"].map((item, i) => (
                  <li key={i} className="pro-footer-list-item" onClick={() => setShowAuth(true)}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="pro-footer-col">
              <h4 className="pro-footer-col-title">Contact Us</h4>
              <p className="pro-footer-contact-desc">Talk to our team about enterprise AI solutions and integrations.</p>
              <div className="pro-footer-contact-list">
                <div className="pro-footer-contact-item">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                  <span>cconeat@aicte-india.org</span>
                </div>
              </div>
              {/* <a href="mailto:cconeat@aicte-india.org" className="pro-footer-demo-btn">Request a Demo</a> */}
            </div>
          </div>
        </div>

        <div className="pro-footer-bottom">
          <span className="pro-footer-copy">© 2026 Anuvadini AgroAI. All rights reserved.</span>
        </div>
      </footer>

    
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
    </div>
  );
};

export default LandingPage;